import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '@heroicons/react/solid';
import getScrollablePortalTarget from '../../../../utils/getScrollablePortalTarget';
import { parseDobString, buildDobString } from '../../../../utils/dobDateUtils';

/**
 * Years offered in the date-of-birth year dropdown for a given age band.
 *
 * minAgeYears is the youngest allowed age (0 = newborn, 18 = adult pathways).
 * maxAgeYears is the oldest allowed age (18 = Well Life Clinic CYP ADHD,
 * 17 = The Owl Centre CYP ADHD, 120 = adults).
 *
 * This is a calendar-year list, not an exact-birthday check. The oldest
 * birth year offered is currentYear - maxAgeYears, so a ceiling of 18
 * includes a child who has had their 18th birthday this year. A 19th
 * birthday falls in an earlier year and is not offered.
 *
 * @param {number} minAgeYears
 * @param {number} maxAgeYears
 * @param {Date} [now]
 * @returns {{ value: string, label: string }[]}
 */
export function getDobYearOptions(minAgeYears = 18, maxAgeYears = 120, now = new Date()) {
  const currentYear = now.getFullYear();
  const youngestBirthYear = currentYear - Number(minAgeYears || 0);
  const oldestBirthYear = currentYear - Number(maxAgeYears || 120);
  const length = Math.max(0, youngestBirthYear - oldestBirthYear + 1);
  return Array.from({ length }, (_, i) => {
    const y = youngestBirthYear - i;
    return { value: String(y), label: String(y) };
  });
}

const DateOfBirthPicker = ({ value, onChange, placeholder, className, minAgeYears = 18, maxAgeYears = 120 }) => {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  // Initialize from value prop. Uses parseDobString (pure string splitting)
  // instead of the Date constructor to avoid UTC/local-time mismatch.
  useEffect(() => {
    if (value) {
      const parts = parseDobString(value);
      if (parts) {
        setDay(parts.day);
        setMonth(parts.month);
        setYear(parts.year);
      }
    } else {
      setDay('');
      setMonth('');
      setYear('');
    }
  }, [value]);

  // Generate arrays for dropdowns
  const days = Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: String(i + 1)
  }));

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const years = getDobYearOptions(minAgeYears, maxAgeYears);

  // Handle value changes
  const handleDayChange = (newDay) => {
    setDay(newDay);
    updateDate(newDay, month, year);
  };

  const handleMonthChange = (newMonth) => {
    setMonth(newMonth);
    updateDate(day, newMonth, year);
  };

  const handleYearChange = (newYear) => {
    setYear(newYear);
    updateDate(day, month, newYear);
  };

  // Validate and emit the date. Uses buildDobString (UTC-safe validation)
  // instead of new Date(isoString) + local-time accessors.
  const updateDate = (d, m, y) => {
    if (d && m && y) {
      const dateString = buildDobString(d, m, y);
      if (dateString) {
        onChange(dateString);
      }
    } else if (!d && !m && !y) {
      onChange('');
    }
  };

  // Custom Select Component — portals the dropdown to a scroll-safe
  // container on <body> (via getScrollablePortalTarget) so it escapes both
  // overflow-clipping scroll containers and CSS-transformed parents (e.g.
  // Radix Dialog with translate).  The portal container restores
  // pointer-events (disabled by Radix DismissableLayer) and intercepts
  // wheel/touchmove events so react-remove-scroll's document-level scroll
  // lock doesn't block the menu from scrolling.
  const CustomSelect = ({ value, onChange, options, placeholder, className: selectClassName }) => {
    const [isSelectOpen, setIsSelectOpen] = useState(false);
    const buttonRef = React.useRef(null);
    const menuRef = React.useRef(null);
    const [menuStyle, setMenuStyle] = useState({});
    const selectedOption = options.find(opt => opt.value === value);

    const openMenu = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const viewportPadding = 16;
        const minDropdownHeight = 120;
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom - viewportPadding;
        const spaceAbove = rect.top - viewportPadding;
        const openAbove = spaceBelow < minDropdownHeight && spaceAbove > spaceBelow;
        const maxAvailableHeight = openAbove ? spaceAbove : spaceBelow;
        const maxHeight = Math.min(Math.max(maxAvailableHeight, minDropdownHeight), viewportHeight * 0.4);

        setMenuStyle({
          position: 'fixed',
          left: rect.left,
          width: rect.width,
          zIndex: 9999,
          maxHeight,
          ...(openAbove
            ? { bottom: viewportHeight - rect.top + 4 }
            : { top: rect.bottom + 4 }),
        });
      }
      setIsSelectOpen(true);
    };

    return (
      <div className={`relative ${selectClassName}`}>
        <button
          ref={buttonRef}
          type="button"
          className="w-full p-2 border rounded-md bg-white text-black text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
          onClick={() => (isSelectOpen ? setIsSelectOpen(false) : openMenu())}
        >
          <span className={selectedOption ? 'text-black' : 'text-gray-500'}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDownIcon 
            className={`h-4 w-4 text-gray-400 transition-transform ${isSelectOpen ? 'rotate-180' : ''}`} 
          />
        </button>
        
        {isSelectOpen && createPortal(
          <>
            {/* Backdrop */}
            <div 
              className="td-dob-picker__menu-portal fixed inset-0"
              style={{ zIndex: 9998 }}
              onClick={() => setIsSelectOpen(false)}
            />
            
            {/* Dropdown */}
            <div
              ref={menuRef}
              className="td-dob-picker__menu-portal bg-white border border-gray-300 rounded-md shadow-lg overflow-y-auto overscroll-contain"
              style={menuStyle}
            >
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-violet-50 focus:bg-violet-50 focus:outline-none text-black"
                  onClick={() => {
                    onChange(option.value);
                    setIsSelectOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </>,
          getScrollablePortalTarget() || document.body
        )}
      </div>
    );
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="grid grid-cols-3 gap-2">
        {/* Day */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Day</label>
          <CustomSelect
            value={day}
            onChange={handleDayChange}
            options={days}
            placeholder="Day"
            className="w-full"
          />
        </div>

        {/* Month */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Month</label>
          <CustomSelect
            value={month}
            onChange={handleMonthChange}
            options={months}
            placeholder="Month"
            className="w-full"
          />
        </div>

        {/* Year */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
          <CustomSelect
            value={year}
            onChange={handleYearChange}
            options={years}
            placeholder="Year"
            className="w-full"
          />
        </div>
      </div>            
    </div>
  );
};

export default DateOfBirthPicker;
